'use client';

import { useState } from 'react';
import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { useUIStore } from '@/store/useUIStore';
import { useAuthContext } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradePrompt } from '@/components/plan/UpgradePrompt';
import { NotificationModal } from '@/components/common/NotificationModal';

export function NavUser({
  user,
}: {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
}) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const notification = useUIStore((state) => state.notification);
  const { subscription } = useSubscription();
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  // 現在のプランID（デフォルトはfree - Issue #1497）
  const currentPlanId = subscription?.planId || 'free';

  // Premiumプランユーザーはアップグレードオプションを非表示
  const showUpgradeOption = currentPlanId !== 'premium';

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-[var(--brand-soft)] data-[state=open]:text-[var(--brand-ink)]"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={`https://avatar.vercel.sh/${user.email}`}
                  alt={user.name}
                />
                <AvatarFallback className="rounded-lg bg-indigo-600 text-white">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              {notification.unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="h-5 w-5 p-0 flex items-center justify-center text-xs animate-pulse"
                >
                  {notification.unreadCount > 9
                    ? '9+'
                    : notification.unreadCount}
                </Badge>
              )}
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={`https://avatar.vercel.sh/${user.email}`}
                    alt={user.name}
                  />
                  <AvatarFallback className="rounded-lg bg-indigo-600 text-white">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {showUpgradeOption && (
              <>
                <DropdownMenuGroup>
                  {/* Issue #2906: onClick → onSelect + preventDefault で Radix の
                      DropdownMenu 閉鎖デフォルト動作 (onPointerDownOutside/
                      onFocusOutside) が新規 Dialog を外側クリックと誤判定して
                      即座にアンマウントする競合を回避。Dialog のマウント完了後に
                      DropdownMenu が閉じるようタイミング分離する。 */}
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setIsUpgradeDialogOpen(true);
                    }}
                  >
                    <Sparkles />
                    プランをアップグレード
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup>
              {/* Issue #2906: onSelect + preventDefault で focus trap 競合回避 (上記コメント参照) */}
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsNotificationModalOpen(true);
                }}
              >
                <Bell />
                通知{' '}
                {notification.unreadCount > 0 &&
                  `(${notification.unreadCount})`}
              </DropdownMenuItem>
              {/* Issue #2908: プラン・請求 (billing タブへ遷移)。通知とアカウントの間に配置。 */}
              <DropdownMenuItem
                onClick={() => navigate('/settings?tab=billing')}
              >
                <CreditCard />
                プラン・請求
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <BadgeCheck />
                アカウント
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              ログアウト
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      {/* アップグレードダイアログ - Issue #798: 現在のプランに基づき上位プランを全て表示 */}
      <UpgradePrompt
        open={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
        currentPlan={{ id: currentPlanId }}
        recommendedPlan={
          currentPlanId === 'free'
            ? 'light'
            : currentPlanId === 'light'
              ? 'standard'
              : 'premium'
        }
      />

      {/* 通知モーダル - Issue #1981 */}
      <NotificationModal
        open={isNotificationModalOpen}
        onOpenChange={setIsNotificationModalOpen}
      />
    </SidebarMenu>
  );
}
